import React, { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import {
  MetadataCollectorActions,
  useMetadataChats,
  useMetadataCollector,
} from '@mint-lib/agentic-ai-circuit';
import { useAiSidebarContext } from '@mint-lib/ai-sidebar-circuit';
import { useTranslation } from '@mint-lib/i18n';
import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom, useSetAtom } from 'jotai';

import { useNotificationWS } from '../../../hooks/useNotificationWS.js';
import { useSuggestionFilter } from '../../../hooks/useSuggestionFilter.js';
import useSuggestionVersion from '../../../hooks/useSuggestionVersion.js';
import {
  useInfiniteSuggestionQuery,
  useReadSuggestion,
} from '../../../queries/suggestions.js';
import {
  activeSuggestionTabAtom,
  selectedSuggestionAtom,
} from '../../../store/index.js';
import { SuggestionFilterActions } from '../../../store/suggestion/suggestionFilter.js';
import SuggestionTabsFilter from '../../atoms/SuggestionTabsFilter/SuggestionTabsFilter.jsx';
import PanelContent from './Content.jsx';
import SuggestionList from './List.jsx';
import ListWrapper from './ListWrapper.jsx';

type SuggestionPanelProps = {
  campaignId?: number;
};

const SuggestionPanel = ({ campaignId }: SuggestionPanelProps) => {
  const { setAgenticMetadata } = useMetadataCollector();
  const { selectedSuggestionFilter, isFilterLoading, setSuggestionFilter } =
    useSuggestionFilter();

  const { findRelatedChats } = useMetadataChats();

  const { isAlphaVersionEnabled } = useSuggestionVersion();

  useEffect(() => {
    setSuggestionFilter({
      type: SuggestionFilterActions.SET_CAMPAIGN_ID,
      data: campaignId,
    });

    if (isAlphaVersionEnabled) {
      setSuggestionFilter({
        type: SuggestionFilterActions.SET_VERSION,
        data: 'alpha',
      });
    }
  }, [isAlphaVersionEnabled, setSuggestionFilter, campaignId]);

  useNotificationWS();

  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const [activeTab, setActiveTab] = useAtom(activeSuggestionTabAtom);

  const { setIsAiSidebarOpen, setCurrentChat } = useAiSidebarContext();

  const { ref, inView } = useInView({
    threshold: 1,
  });

  const { mutate: readSuggestionReq } = useReadSuggestion(
    selectedSuggestionFilter,
  );
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useInfiniteSuggestionQuery({
      ...selectedSuggestionFilter,
    });

  const isEmpty = !data?.pages[0].items.length;

  const newTabQuery = useInfiniteSuggestionQuery({
    ...selectedSuggestionFilter,
    statuses: `${Suggestions.Statuses.ACTIVE},${Suggestions.Statuses.REWORKING}`,
  });

  const hasUnreadSuggestions = useMemo(() => {
    return newTabQuery.data?.pages.some((el) =>
      el.items.some((suggestion) => suggestion.notRead),
    );
  }, [newTabQuery]);

  const setSuggestion = useSetAtom(selectedSuggestionAtom);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [selectedSuggestionFilter, inView, hasNextPage]);

  const collectMetadata = (suggestion: Suggestions.SuggestionResponseType) => {
    if (campaignId) return;

    setAgenticMetadata({
      type: MetadataCollectorActions.SET_RECOMMENDATION,
      data: suggestion,
    });
    findRelatedChats({
      entityId: suggestion.id,
      entityType: 'suggestion',
    });
  };

  const handleSelectCard = (suggestion: Suggestions.SuggestionResponseType) => {
    setSuggestion(suggestion);
    collectMetadata(suggestion);
    setIsAiSidebarOpen(false);
    setCurrentChat(null);

    if (suggestion.notRead) {
      readSuggestionReq(suggestion);
    }
  };

  return (
    <ListWrapper isRecoRobot={!!campaignId} key={activeTab}>
      <div
        className={cn(
          'flex items-baseline justify-between gap-2',
          campaignId ? 'flex-row' : 'flex-col',
        )}
      >
        <p className={cn(typographyVariants({ variant: 'h3' }), 'mb-3')}>
          {t('Suggestion Notification')}
        </p>
        <SuggestionTabsFilter
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hasUnreadSuggestions={hasUnreadSuggestions}
        />
      </div>
      <div
        className={cn(
          'mt-4 flex flex-col gap-4 overflow-y-auto p-px',
          campaignId ? 'h-[calc(100vh-360px)]' : 'h-[calc(100vh-472px)]',
        )}
        data-testid="suggestion-list"
      >
        <PanelContent
          isLoading={isLoading || isFilterLoading}
          isEmpty={isEmpty}
          isRecoRobot={!!campaignId}
        >
          <SuggestionList
            ref={ref}
            data={data}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            handleSelectCard={handleSelectCard}
            campaignId={campaignId}
          />
        </PanelContent>
      </div>
    </ListWrapper>
  );
};

export default SuggestionPanel;
